import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { TENANT_LIFECYCLE_TEXT, loginResponseSchema, type LoginResponse } from '@castinflow/shared';
import { Alert, Button, Field, Input } from '@castinflow/ui';
import { Fingerprint } from 'lucide-react';
import { ApiError, apiFetch } from '../lib/api';
import { passkeyLoginOptions, passkeyLoginVerify } from '../framework/account/account-api';
import { useSession } from '../lib/session';
import { useSignupStatus } from '../lib/signup-status';
import { usePageMeta } from '../shell/page-meta';
import { PublicCard } from '../shell/PublicLayout';
import { tr } from '../i18n/tr';
import { LIFECYCLE_ROUTES, lifecycleNav } from '../framework/lifecycle/lifecycle-nav';

/** IP izin listesi reddi (403 + `details.reason`): kimlik hatasından ayrı, açık bir metinle gösterilir. */
const isIpDenied = (e: unknown): boolean =>
  e instanceof ApiError &&
  e.status === 403 &&
  (e.details as { reason?: string } | undefined)?.reason === 'ip_not_allowed';

/**
 * Giriş: çalışma alanı adresi + e-posta + parola; 2FA etkinse ikinci adım (TOTP veya kurtarma kodu). Passkey ile giriş
 * (M6 WP 6.2): çalışma alanı adresi + cihazdaki passkey; e-posta/parola istenmez ve ikinci adım gerekmez.
 */
export function LoginPage() {
  const nav = useNavigate();
  const setMe = useSession((s) => s.setMe);
  const [tenantSlug, setTenantSlug] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [challengeToken, setChallengeToken] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  const signupStatus = useSignupStatus();
  usePageMeta(challengeToken ? tr.auth.totpTitle : tr.auth.loginTitle);

  async function onPasskey() {
    const slug = tenantSlug.trim().toLowerCase();
    setError(null);
    if (slug === '') {
      setError(tr.auth.passkeyNeedsWorkspace);
      return;
    }
    setPasskeyBusy(true);
    try {
      // WebAuthn kitaplığı yalnız bu eylemde yüklenir (ilk pakete girmez)
      const webauthn = await import('@simplewebauthn/browser');
      if (!webauthn.browserSupportsWebAuthn()) {
        setError(tr.auth.passkeyUnsupported);
        return;
      }
      const { challengeId, options } = await passkeyLoginOptions(slug);
      const response = await webauthn.startAuthentication({
        optionsJSON: options as unknown as Parameters<
          typeof webauthn.startAuthentication
        >[0]['optionsJSON'],
      });
      const res = loginResponseSchema.parse(await passkeyLoginVerify(challengeId, response));
      if (res.status !== 'ok') {
        setError(tr.auth.passkeyFailed);
        return;
      }
      setMe(res.me);
      nav('/');
    } catch (e) {
      if (isIpDenied(e)) setError(tr.auth.ipNotAllowed);
      else if (e instanceof ApiError && e.code === 'RATE_LIMITED') setError(tr.auth.rateLimited);
      else if (e instanceof ApiError && e.code === 'TENANT_SUSPENDED')
        setError(TENANT_LIFECYCLE_TEXT.suspended);
      else setError(tr.auth.passkeyFailed);
    } finally {
      setPasskeyBusy(false);
    }
  }

  async function submit(path: string, body: unknown) {
    setBusy(true);
    setError(null);
    try {
      const raw = await apiFetch<LoginResponse>(path, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      });
      const res = loginResponseSchema.parse(raw);
      if (res.status === 'totp_required') {
        setChallengeToken(res.challengeToken);
        return;
      }
      setMe(res.me);
      nav('/');
    } catch (e) {
      // Teknik hata metni kullanıcıya gösterilmez; kimlik hatası hangi alanın yanlış olduğunu da söylemez
      if (e instanceof ApiError && e.code === 'RATE_LIMITED') setError(tr.auth.rateLimited);
      else if (isIpDenied(e)) setError(tr.auth.ipNotAllowed);
      // v3.1 B3: askıdaki çalışma alanı (yalnız doğru parolayla bildirilir; çalışma alanının varlığını sızdırmaz)
      else if (e instanceof ApiError && e.code === 'TENANT_SUSPENDED')
        setError(TENANT_LIFECYCLE_TEXT.suspended);
      else if (e instanceof ApiError && (e.code === 'UNAUTHENTICATED' || e.status === 401))
        setError(tr.auth.invalidCredentials);
      else setError(tr.auth.genericError);
    } finally {
      setBusy(false);
    }
  }

  function onLogin(e: FormEvent) {
    e.preventDefault();
    void submit('/auth/login', { tenantSlug: tenantSlug.trim().toLowerCase(), email, password });
  }
  function onSecond(e: FormEvent) {
    e.preventDefault();
    if (useRecovery) void submit('/auth/login/recovery', { challengeToken, recoveryCode: code });
    else void submit('/auth/login/totp', { challengeToken, code });
  }

  const errorAlert = error ? (
    <div data-testid="login-error">
      <Alert tone="danger" title={error} announce />
    </div>
  ) : null;

  if (challengeToken) {
    return (
      <PublicCard>
        <form onSubmit={onSecond} className="space-y-4" data-testid="totp-form" noValidate>
          <h1
            tabIndex={-1}
            className="text-xl font-semibold text-fg focus:outline-none"
            data-page-title=""
          >
            {tr.auth.totpTitle}
          </h1>
          <Field label={useRecovery ? tr.auth.recoveryCode : tr.auth.totpCode}>
            <Input
              value={code}
              onChange={(e) => {
                setCode(e.target.value);
              }}
              inputMode={useRecovery ? 'text' : 'numeric'}
              autoComplete="one-time-code"
              data-testid="totp-code"
            />
          </Field>
          {errorAlert}
          <div className="flex flex-wrap items-center gap-3">
            <Button type="submit" variant="primary" loading={busy} data-testid="totp-submit">
              {tr.auth.verify}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setUseRecovery((v) => !v);
                setCode('');
              }}
            >
              {useRecovery ? tr.auth.useTotp : tr.auth.useRecovery}
            </Button>
          </div>
        </form>
      </PublicCard>
    );
  }

  return (
    <PublicCard>
      <form onSubmit={onLogin} className="space-y-4" data-testid="login-form" noValidate>
        <div>
          <h1
            tabIndex={-1}
            className="text-xl font-semibold text-fg focus:outline-none"
            data-page-title=""
          >
            {tr.auth.loginTitle}
          </h1>
          <p className="mt-1 text-base text-fg-muted">{tr.auth.loginIntro}</p>
        </div>
        <Field label={tr.auth.workspaceAddress}>
          <Input
            value={tenantSlug}
            onChange={(e) => {
              setTenantSlug(e.target.value);
            }}
            autoComplete="organization"
            autoCapitalize="none"
            spellCheck={false}
            data-testid="login-slug"
          />
        </Field>
        <Field label={tr.auth.email}>
          <Input
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
            }}
            autoComplete="username"
            data-testid="login-email"
          />
        </Field>
        <Field label={tr.auth.password}>
          <Input
            type="password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
            }}
            autoComplete="current-password"
            data-testid="login-password"
          />
        </Field>
        {errorAlert}
        <Button
          type="submit"
          variant="primary"
          className="w-full"
          loading={busy}
          data-testid="login-submit"
        >
          {tr.auth.submitLogin}
        </Button>
        {/* M7 T2: parola sıfırlama bağlantısı isteme sayfası (oturumsuz) */}
        <p className="text-sm text-fg-muted">
          <Link
            to="/forgot-password"
            className="text-primary underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
            data-testid="login-forgot"
          >
            {tr.auth.forgotLink}
          </Link>
        </p>
        <div className="space-y-2" data-testid="login-passkey-block">
          {/* Ayraç çizgileri dekoratif; okunan metin yalnız "ya da" */}
          <p className="flex items-center gap-3 text-sm text-fg-subtle">
            <span aria-hidden="true" className="h-px flex-1 bg-border" />
            {tr.auth.or}
            <span aria-hidden="true" className="h-px flex-1 bg-border" />
          </p>
          <Button
            className="w-full"
            loading={passkeyBusy}
            leadingIcon={<Fingerprint size={16} aria-hidden="true" />}
            onClick={() => {
              void onPasskey();
            }}
            data-testid="login-passkey"
          >
            {tr.auth.passkeyLogin}
          </Button>
        </div>
        {/* Hesap bağlantıları kartın alt şeridinde (v3.5); sıra ve koşullar aynen */}
        <div className="space-y-2 border-t border-border pt-4">
          {/* M7 T1: kayıt bağlantısı yalnız sunucu kaydın açık olduğunu bildirdiyse (kapalı ya da okunamadıysa gösterilmez) */}
          {signupStatus.data?.open === true ? (
            <p className="text-base text-fg-muted" data-testid="login-signup-link">
              {tr.auth.noAccount}{' '}
              <Link
                to="/signup"
                className="font-medium text-primary underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
              >
                {tr.auth.createAccount}
              </Link>
            </p>
          ) : null}
          <p className="text-sm text-fg-muted">
            <Link
              to={LIFECYCLE_ROUTES.cancelClosure}
              className="text-primary underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
              data-testid="login-closure-cancel"
            >
              {lifecycleNav.cancelClosureLink}
            </Link>
          </p>
        </div>
      </form>
    </PublicCard>
  );
}
